import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { FileText, Download, Trash, Plus, X, FloppyDisk, ArrowsClockwise, Eye, Tag } from "@phosphor-icons/react";

// Admin audit + operations manual store. Backed by the `admin_docs`
// collection. All actions require an admin session. We keep the file bytes
// inline on the doc so the download stream is a simple base64→bytes hop
// (files are capped at 15 MB server-side, comfortably under Mongo's 16 MB
// document limit). Text-like documents (`.md`, `.txt`) also expose a
// `content_md` field that we render inline as read-only markdown.

const CATEGORIES = [
    { value: "audit", label: "Audit" },
    { value: "manual", label: "Operations Manual" },
    { value: "policy", label: "Policy" },
    { value: "other", label: "Other" },
];

const catLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || v;

function humanSize(n) {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

// Lightweight markdown renderer for the viewer modal. We only need
// headings, paragraphs, unordered lists, inline **bold** / *italic* /
// `code`, and fenced code blocks — anything richer belongs in a real
// renderer and is out of scope for an admin-only reader.
function renderMd(md) {
    if (!md) return null;
    const lines = md.replace(/\r\n/g, "\n").split("\n");
    const out = [];
    let list = [];
    let code = null;

    const flushList = () => {
        if (list.length) {
            out.push(
                <ul key={`ul-${out.length}`} className="list-disc pl-6 my-3 space-y-1">
                    {list.map((t, i) => (
                        <li key={i} className="text-[#d4d4d4]">{inline(t)}</li>
                    ))}
                </ul>
            );
            list = [];
        }
    };

    const inline = (s) => {
        const parts = [];
        let rest = s;
        // Inline code
        const codeSplit = rest.split(/(`[^`]+`)/g);
        codeSplit.forEach((seg, i) => {
            if (/^`[^`]+`$/.test(seg)) {
                parts.push(
                    <code key={`c-${i}`} className="bg-[#141414] border border-[#2a2a2a] px-1.5 py-0.5 text-[12px] font-mono text-[#2ec5b6]">
                        {seg.slice(1, -1)}
                    </code>
                );
            } else {
                // Bold, then italic
                const b = seg.split(/(\*\*[^*]+\*\*)/g);
                b.forEach((sub, j) => {
                    if (/^\*\*[^*]+\*\*$/.test(sub)) {
                        parts.push(<strong key={`b-${i}-${j}`}>{sub.slice(2, -2)}</strong>);
                    } else {
                        const it = sub.split(/(\*[^*]+\*)/g);
                        it.forEach((k, ki) => {
                            if (/^\*[^*]+\*$/.test(k)) {
                                parts.push(<em key={`i-${i}-${j}-${ki}`}>{k.slice(1, -1)}</em>);
                            } else {
                                parts.push(k);
                            }
                        });
                    }
                });
            }
        });
        return parts;
    };

    lines.forEach((raw, idx) => {
        const line = raw.replace(/\s+$/g, "");
        if (line.startsWith("```")) {
            if (code === null) {
                code = { buf: [] };
            } else {
                out.push(
                    <pre key={`pre-${idx}`} className="bg-[#0a0a0a] border border-[#2a2a2a] p-3 my-3 overflow-x-auto text-[12px] font-mono text-[#c2c2c2]">
                        <code>{code.buf.join("\n")}</code>
                    </pre>
                );
                code = null;
            }
            return;
        }
        if (code) {
            code.buf.push(raw);
            return;
        }
        if (!line.trim()) {
            flushList();
            return;
        }
        if (line.startsWith("### ")) {
            flushList();
            out.push(<h3 key={idx} className="font-display text-lg mt-5 mb-2 text-white">{line.slice(4)}</h3>);
            return;
        }
        if (line.startsWith("## ")) {
            flushList();
            out.push(<h2 key={idx} className="font-display text-xl mt-6 mb-2 text-white">{line.slice(3)}</h2>);
            return;
        }
        if (line.startsWith("# ")) {
            flushList();
            out.push(<h1 key={idx} className="font-display text-2xl mt-6 mb-3 text-white">{line.slice(2)}</h1>);
            return;
        }
        if (/^\s*[-*]\s+/.test(line)) {
            list.push(line.replace(/^\s*[-*]\s+/, ""));
            return;
        }
        flushList();
        out.push(<p key={idx} className="text-[#d4d4d4] my-2 leading-relaxed">{inline(line)}</p>);
    });
    flushList();
    if (code) {
        out.push(
            <pre key="pre-tail" className="bg-[#0a0a0a] border border-[#2a2a2a] p-3 my-3 overflow-x-auto text-[12px] font-mono text-[#c2c2c2]">
                <code>{code.buf.join("\n")}</code>
            </pre>
        );
    }
    return out;
}

export default function AdminDocsPanel() {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const [viewer, setViewer] = useState(null); // full doc when viewing
    const [uploading, setUploading] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [form, setForm] = useState({ title: "", category: "audit", description: "", tags: "" });
    const fileRef = useRef(null);

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const { data } = await api.get("/admin/docs");
            setDocs(data);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const grouped = useMemo(() => {
        const g = { audit: [], manual: [], policy: [], other: [] };
        docs.forEach((d) => {
            (g[d.category] = g[d.category] || []).push(d);
        });
        return g;
    }, [docs]);

    const openViewer = async (docId) => {
        setErr("");
        try {
            const { data } = await api.get(`/admin/docs/${docId}`);
            setViewer(data);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const download = async (docId, filename) => {
        setErr("");
        try {
            const res = await api.get(`/admin/docs/${docId}/download`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = filename || "document";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const remove = async (doc) => {
        if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
        setErr("");
        try {
            await api.delete(`/admin/docs/${doc.id}`);
            setMsg(`Deleted "${doc.title}".`);
            await load();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const submitUpload = async (e) => {
        e.preventDefault();
        if (!fileRef.current?.files?.[0]) {
            setErr("Please choose a file to upload.");
            return;
        }
        if (!form.title.trim()) {
            setErr("Please give this document a title.");
            return;
        }
        setUploading(true);
        setErr("");
        setMsg("");
        try {
            const fd = new FormData();
            fd.append("file", fileRef.current.files[0]);
            fd.append("title", form.title.trim());
            fd.append("category", form.category);
            fd.append("description", form.description.trim());
            fd.append("tags", form.tags.trim());
            await api.post("/admin/docs", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setMsg(`Uploaded "${form.title.trim()}".`);
            setForm({ title: "", category: "audit", description: "", tags: "" });
            if (fileRef.current) fileRef.current.value = "";
            setShowUpload(false);
            await load();
        } catch (ex) {
            setErr(formatApiError(ex));
        } finally {
            setUploading(false);
        }
    };

    return (
        <div data-testid="admin-docs-panel">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="font-display text-2xl">Documents &amp; Audits</div>
                    <div className="text-sm text-[#9a9a9a] mt-1">
                        Retain periodic RAG/LLM audits, operations manuals, and governance evidence in one place.
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        className="btn-secondary inline-flex items-center gap-2"
                        onClick={load}
                        data-testid="admin-docs-refresh"
                    >
                        <ArrowsClockwise size={14} weight="bold" /> Refresh
                    </button>
                    <button
                        className="btn-primary inline-flex items-center gap-2"
                        onClick={() => setShowUpload((v) => !v)}
                        data-testid="admin-docs-upload-toggle"
                    >
                        <Plus size={14} weight="bold" /> {showUpload ? "Close" : "Upload document"}
                    </button>
                </div>
            </div>

            {msg && <div className="block-opportunity mb-4 text-sm" data-testid="admin-docs-msg">{msg}</div>}
            {err && <div className="block-risk mb-4 text-sm" data-testid="admin-docs-error">{err}</div>}

            {showUpload && (
                <form
                    onSubmit={submitUpload}
                    className="border border-[#2a2a2a] bg-[#0a0a0a] p-5 mb-8 grid grid-cols-12 gap-3"
                    data-testid="admin-docs-upload-form"
                >
                    <label className="col-span-12 md:col-span-6 text-xs uppercase tracking-widest text-[#9a9a9a]">
                        Title
                        <input
                            className="input-base mt-1"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="e.g. Q3 2026 RAG Grounding Audit"
                            data-testid="admin-docs-title"
                        />
                    </label>
                    <label className="col-span-12 md:col-span-3 text-xs uppercase tracking-widest text-[#9a9a9a]">
                        Category
                        <select
                            className="input-base mt-1"
                            value={form.category}
                            onChange={(e) => setForm({ ...form, category: e.target.value })}
                            data-testid="admin-docs-category"
                        >
                            {CATEGORIES.map((c) => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                        </select>
                    </label>
                    <label className="col-span-12 md:col-span-3 text-xs uppercase tracking-widest text-[#9a9a9a]">
                        Tags (comma-separated)
                        <input
                            className="input-base mt-1"
                            value={form.tags}
                            onChange={(e) => setForm({ ...form, tags: e.target.value })}
                            placeholder="rag, grounding, q3-2026"
                            data-testid="admin-docs-tags"
                        />
                    </label>
                    <label className="col-span-12 text-xs uppercase tracking-widest text-[#9a9a9a]">
                        Description (optional)
                        <textarea
                            rows={2}
                            className="input-base mt-1"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="What this document covers and when it was produced."
                            data-testid="admin-docs-description"
                        />
                    </label>
                    <label className="col-span-12 md:col-span-8 text-xs uppercase tracking-widest text-[#9a9a9a]">
                        File (.md, .txt, .pdf, .docx, …up to 15 MB)
                        <input
                            ref={fileRef}
                            type="file"
                            className="input-base mt-1 file:mr-3 file:py-1 file:px-2 file:border-0 file:bg-[#2ec5b6] file:text-[#0a1f1d] file:font-bold file:uppercase file:text-xs file:tracking-widest"
                            data-testid="admin-docs-file"
                        />
                    </label>
                    <div className="col-span-12 md:col-span-4 flex items-end justify-end gap-2">
                        <button
                            type="button"
                            className="btn-secondary inline-flex items-center gap-1"
                            onClick={() => setShowUpload(false)}
                            disabled={uploading}
                        >
                            <X size={14} /> Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-primary inline-flex items-center gap-1"
                            disabled={uploading}
                            data-testid="admin-docs-submit"
                        >
                            <FloppyDisk size={14} /> {uploading ? "Uploading…" : "Save document"}
                        </button>
                    </div>
                </form>
            )}

            {loading ? (
                <div className="bg-[#0a0a0a] border border-[#2a2a2a] p-6 text-[#c2c2c2]">Loading documents…</div>
            ) : docs.length === 0 ? (
                <div className="bg-[#0a0a0a] border border-[#2a2a2a] p-6 text-[#c2c2c2]" data-testid="admin-docs-empty">
                    No documents yet. Upload the first audit or operations manual to get started.
                </div>
            ) : (
                CATEGORIES.map((cat) => {
                    const rows = grouped[cat.value] || [];
                    if (rows.length === 0) return null;
                    return (
                        <section key={cat.value} className="mb-8" data-testid={`admin-docs-cat-${cat.value}`}>
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="font-display text-xl">{cat.label}</h2>
                                <div className="text-xs text-[#9a9a9a]">{rows.length} document{rows.length === 1 ? "" : "s"}</div>
                            </div>
                            <div className="border border-[#2a2a2a] divide-y divide-[#2a2a2a]">
                                {rows.map((d) => (
                                    <div
                                        key={d.id}
                                        className="bg-[#0a0a0a] p-4 grid grid-cols-12 gap-3 items-start hover:bg-[#141414]"
                                        data-testid={`admin-doc-row-${d.id}`}
                                    >
                                        <div className="col-span-12 md:col-span-6">
                                            <div className="flex items-center gap-2">
                                                <FileText size={16} weight="bold" className="text-[#2ec5b6]" />
                                                <div className="font-display text-lg text-white">{d.title}</div>
                                                {d.version > 1 && (
                                                    <span className="text-[10px] uppercase tracking-widest bg-[#141414] border border-[#2a2a2a] text-[#9a9a9a] px-2 py-0.5">
                                                        v{d.version}
                                                    </span>
                                                )}
                                            </div>
                                            {d.description && (
                                                <div className="text-sm text-[#c2c2c2] mt-1">{d.description}</div>
                                            )}
                                            <div className="text-xs text-[#9a9a9a] mt-1 font-mono">
                                                {d.filename} · {humanSize(d.size_bytes)}
                                            </div>
                                            {d.tags?.length ? (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {d.tags.map((t) => (
                                                        <span
                                                            key={t}
                                                            className="inline-flex items-center gap-1 text-[11px] bg-[#141414] border border-[#2a2a2a] text-[#c2c2c2] px-2 py-0.5"
                                                        >
                                                            <Tag size={10} /> {t}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : null}
                                        </div>
                                        <div className="col-span-6 md:col-span-3 text-xs text-[#9a9a9a]">
                                            <div>Uploaded by {d.uploaded_by || "system"}</div>
                                            <div className="mt-1">{d.created_at ? new Date(d.created_at).toLocaleString() : "—"}</div>
                                            {d.updated_at && d.updated_at !== d.created_at && (
                                                <div className="mt-1 text-[#2ec5b6]">Updated {new Date(d.updated_at).toLocaleString()}</div>
                                            )}
                                        </div>
                                        <div className="col-span-6 md:col-span-3 flex flex-wrap gap-1 justify-end">
                                            {d.is_text && (
                                                <button
                                                    className="btn-ghost inline-flex items-center gap-1"
                                                    onClick={() => openViewer(d.id)}
                                                    data-testid={`admin-doc-view-${d.id}`}
                                                    title="Read inline"
                                                >
                                                    <Eye size={14} /> View
                                                </button>
                                            )}
                                            <button
                                                className="btn-ghost inline-flex items-center gap-1"
                                                onClick={() => download(d.id, d.filename)}
                                                data-testid={`admin-doc-download-${d.id}`}
                                                title="Download original file"
                                            >
                                                <Download size={14} /> Download
                                            </button>
                                            <button
                                                className="btn-ghost inline-flex items-center gap-1 text-[#ff6b6b] hover:text-[#ff9b9b]"
                                                onClick={() => remove(d)}
                                                data-testid={`admin-doc-delete-${d.id}`}
                                                title="Delete document"
                                            >
                                                <Trash size={14} /> Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    );
                })
            )}

            {viewer && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 md:p-10 overflow-y-auto"
                    onClick={() => setViewer(null)}
                    data-testid="admin-doc-viewer"
                >
                    <div
                        className="bg-[#0a0a0a] border border-[#2a2a2a] max-w-4xl w-full p-6 md:p-8"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-[#2ec5b6]">{catLabel(viewer.category)}</div>
                                <h2 className="font-display text-2xl text-white mt-1">{viewer.title}</h2>
                                {viewer.description && (
                                    <p className="text-sm text-[#c2c2c2] mt-2 max-w-2xl">{viewer.description}</p>
                                )}
                                <div className="text-xs text-[#9a9a9a] mt-2 font-mono">
                                    {viewer.filename} · {humanSize(viewer.size_bytes)} · v{viewer.version}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    className="btn-secondary inline-flex items-center gap-1"
                                    onClick={() => download(viewer.id, viewer.filename)}
                                    data-testid="admin-doc-viewer-download"
                                >
                                    <Download size={14} /> Download
                                </button>
                                <button
                                    className="btn-ghost"
                                    onClick={() => setViewer(null)}
                                    data-testid="admin-doc-viewer-close"
                                    aria-label="Close viewer"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>
                        <div className="border-t border-[#2a2a2a] pt-5">
                            {viewer.is_text ? (
                                <article className="max-w-none">{renderMd(viewer.content_md || "")}</article>
                            ) : (
                                <div className="text-[#c2c2c2]">
                                    Binary document. Use the Download button to open it locally.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
